// 플랫폼 결제 왕복의 규칙 — 순수 함수 (플랫폼 README 「MVP 가 결제를 붙이는 법」)
//   1. 화면 → POST <플랫폼>/v1/checkout/sessions {product, return_to}  → {url}
//   2. 유저를 url(플랫폼 결제 페이지)로 보낸다
//   3. 플랫폼 → <return_to>?platform_checkout=pc_…&platform_checkout_status=<상태>
// 3 의 상태는 화면 표시용이다 — 누구나 주소에 적을 수 있다. 권한은 MVP API 가 받은 서명 알림으로만 준다

export const CHECKOUT_STATUSES = ['pending', 'paid', 'cancelled', 'expired', 'failed', 'refunded'] as const;

export type CheckoutStatus = (typeof CHECKOUT_STATUSES)[number];

export interface CheckoutReturn {
  readonly id: string;
  readonly status: CheckoutStatus;
}

const ID_KEY = 'platform_checkout';
const STATUS_KEY = 'platform_checkout_status';

/** 결제에서 돌아온 주소면 결과를, 아니면 null. rest 는 결제 값을 뺀 쿼리 (`?` 포함, 없으면 빈 문자열) */
export function readReturn(search: string): { result: CheckoutReturn; rest: string } | null {
  const params = new URLSearchParams(search);
  const id = params.get(ID_KEY);
  const status = params.get(STATUS_KEY);
  if (!id || !/^pc_[A-Za-z0-9_-]{1,64}$/.test(id)) return null;
  if (!CHECKOUT_STATUSES.includes(status as CheckoutStatus)) return null;
  params.delete(ID_KEY);
  params.delete(STATUS_KEY);
  const rest = params.toString();
  return { result: { id, status: status as CheckoutStatus }, rest: rest ? `?${rest}` : '' };
}

/** 결제 뒤 돌아올 주소 — 지금 주소에서 조각과 지난 결제 값을 뗀다 */
export function returnToOf(href: string): string {
  const url = new URL(href);
  url.hash = '';
  url.searchParams.delete(ID_KEY);
  url.searchParams.delete(STATUS_KEY);
  return url.toString();
}

/** 결제 페이지 주소가 플랫폼 것인지 — 응답이 가리키는 다른 곳으로 유저를 보내지 않는다 */
export function isPlatformUrl(url: string, platformUrl: string): boolean {
  try {
    return new URL(url).origin === new URL(platformUrl).origin;
  } catch {
    return false;
  }
}

export const CHECKOUT_ERRORS = ['login_required', 'product_not_found', 'payments_not_enabled', 'failed'] as const;

export type CheckoutError = (typeof CHECKOUT_ERRORS)[number];

/** 플랫폼 응답(HTTP 상태·error 코드)을 화면이 구분할 사유로 줄인다 */
export function toCheckoutError(status: number, code: unknown): CheckoutError {
  if (status === 401) return 'login_required';
  if (status === 404 && code === 'product_not_found') return 'product_not_found';
  if (status === 503 && code === 'payments_not_enabled') return 'payments_not_enabled';
  return 'failed';
}
